import { ArrowLeft, ChevronDown, ChevronUp, FileText, AlertTriangle, Briefcase, Mail, CheckCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { CURRENT_TERMS_VERSION, TERMS_CHANGELOG } from '../utils/terms-version';
import { PageShell } from './layout/PageShell';

interface TermsOfServicePageProps {
  onBack: () => void;
  userType: 'Talent' | 'Arbeidsgiver';
  onWithdrawalForm?: () => void;
  /** When false, show "Logg inn for å bruke angreskjema" instead of navigating to angrerett (avoids restricted redirect). */
  isLoggedIn?: boolean;
  /** Called when unauthenticated user wants to use angreskjema; typically navigate to auth. */
  onLoginRequired?: () => void;
  /** Called when user clicks link to Personvernerklæring; typically navigate to privacy. */
  onNavigateToPrivacy?: () => void;
}

export function TermsOfServicePage({ onBack, userType, onWithdrawalForm, isLoggedIn = true, onLoginRequired, onNavigateToPrivacy }: TermsOfServicePageProps) {
  const [showAngreForm, setShowAngreForm] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50">
      {/* Content - scroll handled by main / App wrapper */}
      <PageShell className="pt-page-safe-sm pb-android-pwa-doc xl:max-w-4xl xl:mx-auto xl:px-4 xl:px-8 xl:pt-5 xl:pb-24">
          {/* Content container - fullscreen on mobile, rounded modal on desktop */}
          <div className="pt-5 xl:bg-white xl:rounded-3xl xl:shadow-xl xl:border-2 xl:border-purple-100 xl:p-8">
            {/* Back button */}
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 px-4 xl:px-0 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Tilbake</span>
            </button>

            {/* Header */}
            <h1 className="text-3xl sm:text-4xl mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1 px-4 xl:px-0">
              Vilkår og betingelser
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 px-4 xl:px-0">
              <p className="text-gray-600">
                Sist oppdatert: {TERMS_CHANGELOG[CURRENT_TERMS_VERSION]?.date ?? 'Lansering 2025'}
              </p>
              <p className="text-sm text-purple-600 bg-purple-50 px-3 py-1 rounded-full border border-purple-200">
                Versjon {CURRENT_TERMS_VERSION}
              </p>
            </div>

            {/* Changelog – only show when there are entries (post-launch terms updates) */}
            {Object.keys(TERMS_CHANGELOG).length > 0 && (
              <>
                <button
                  onClick={() => setShowAngreForm(!showAngreForm)}
                  className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 px-4 xl:px-0 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  <span>{showAngreForm ? 'Skjul endringslogg' : 'Vis endringslogg'}</span>
                </button>
                {showAngreForm && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl border-2 border-purple-200 mb-8 mx-4 xl:mx-0">
                    <h2 className="text-2xl text-purple-900 mb-4">Endringslogg</h2>
                    <div className="space-y-6">
                      {Object.entries(TERMS_CHANGELOG).reverse().map(([version, info]) => (
                        <div key={version} className="bg-white p-4 rounded-xl border border-purple-100">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-lg text-purple-900">Versjon {version}</h3>
                            <span className="text-sm text-gray-600">{info.date}</span>
                          </div>
                          <ul className="space-y-1 text-gray-700">
                            {info.changes.map((change, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-purple-600 mt-1">•</span>
                                <span>{change}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Content */}
            <div className="prose prose-purple max-w-none space-y-6 px-4 xl:px-0">
              {/* Introduction */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">1. Aksept av vilkår</h2>
                <p className="text-gray-700 leading-relaxed">
                  Ved å opprette en konto og bruke Talentfunn ("Tjenesten"), godtar du å være bundet av disse vilkårene og betingelsene. Hvis du ikke godtar disse vilkårene, må du ikke bruke tjenesten.
                </p>
                <h3 className="text-xl text-purple-800 mb-2 mt-4">1.1 Personvern</h3>
                <p className="text-gray-700 leading-relaxed">
                  Vår behandling av dine personopplysninger er beskrevet i vår{' '}
                  {onNavigateToPrivacy ? (
                    <button
                      type="button"
                      onClick={onNavigateToPrivacy}
                      className="text-purple-600 hover:text-purple-700 underline font-medium"
                    >
                      Personvernerklæring
                    </button>
                  ) : (
                    <span className="text-purple-600 font-medium">Personvernerklæring</span>
                  )}
                  . Ved å bruke tjenesten bekrefter du at du har lest og forstått hvordan vi behandler dine data.
                </p>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Vi forbeholder oss retten til å endre disse vilkårene til enhver tid. Mindre endringer trer i kraft umiddelbart etter publisering på plattformen. Vesentlige endringer vil bli varslet minst 30 dager før de trer i kraft (jf. punkt 13).
                </p>
              </section>

              {/* Account */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">2. Brukerkontoer</h2>
                <h3 className="text-xl text-purple-800 mb-2">2.1 Registrering</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  For å bruke Talentfunn må du opprette en konto med gyldig e-postadresse og passord. Du må være minst 15 år gammel for å bruke tjenesten.
                </p>
                
                <h3 className="text-xl text-purple-800 mb-2">2.2 Mindreårige brukere (under 18 år)</h3>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-3">
                  <p className="text-gray-700 leading-relaxed mb-2">
                    Brukere under 18 år plikter å informere foresatte om bruk av Talentfunn som gratisbruker. For å inngå avtale om betalte tjenester (Pluss-abonnement) må du være over 18 år. Talentfunn forbeholder seg retten til å stenge kontoer eller kansellere abonnementer der vi avdekker at kjøper er under 18 år.
                  </p>
                </div>
                
                <h3 className="text-xl text-purple-800 mb-2">2.3 Kontosikkerhet</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Du er ansvarlig for å holde din innloggingsinformasjon konfidensiell og for all aktivitet som skjer under din konto. Du samtykker i å varsle oss umiddelbart om enhver uautorisert bruk av din konto.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">2.4 Brukertyper</h3>
                <p className="text-gray-700 leading-relaxed">
                  Talentfunn tilbyr to brukertyper:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-2">
                  <li><strong>Talent:</strong> Personer som ønsker å vise frem sine ferdigheter og prosjekter</li>
                  <li><strong>Arbeidsgiver:</strong> Personer eller bedrifter som søker etter talenter å ansette</li>
                </ul>
              </section>

              {/* Employer Verification */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">3. Spesielle vilkår for arbeidsgivere</h2>
                
                <h3 className="text-xl text-purple-800 mb-2">3.1 Representasjon av bedrift</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Ved å registrere deg som arbeidsgiver på Talentfunn, bekrefter og garanterer du at:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                  <li>Du har <strong>myndighet og fullmakt</strong> til å representere den angitte bedriften</li>
                  <li>Du er ansatt i, eier, eller på annen måte har <strong>legitim tilknytning</strong> til bedriften</li>
                  <li>Bedriften har <strong>godkjent</strong> at du oppretter profil og bruker tjenesten på deres vegne</li>
                  <li>All informasjon du oppgir om bedriften er <strong>korrekt og oppdatert</strong></li>
                  <li>E-postadressen du bruker tilhører deg og/eller bedriften</li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2">3.2 Organisasjonsnummer og verifisering</h3>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-3">
                  <p className="text-gray-800 font-medium mb-2">
                    Alle arbeidsgivere må oppgi gyldig organisasjonsnummer ved registrering.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Organisasjonsnummeret valideres automatisk mot Brønnøysundregisteret for å bekrefte at bedriften eksisterer og er aktiv.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Du må også oppgi din rolle i bedriften (f.eks. daglig leder, styreleder, etc.) for å hjelpe oss med verifisering.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">3.3 E-postverifisering</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Alle arbeidsgivere må verifisere sin e-postadresse ved å klikke på en lenke i e-posten du mottar ved registrering. Dette bekrefter at du har tilgang til e-postadressen og styrker verifiseringen av din identitet.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">3.4 Manuell godkjenning</h3>
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-3">
                  <p className="text-gray-800 font-medium mb-2">
                    ⏳ Arbeidsgiver-kontoer krever manuell godkjenning fra Talentfunns administratorer.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Etter registrering og e-postverifisering, vil vi manuelt verifisere at du har legitimt til å representere bedriften. Dette kan ta opptil 1 time.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Under godkjenningsprosessen kan du:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-3">
                  <li>Logge inn og se talenter på plattformen</li>
                  <li>Utforske funksjonaliteten</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Du kan <strong>ikke</strong>:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-3">
                  <li>Sende meldinger til talenter</li>
                  <li>Kjøpe abonnement</li>
                  <li>Lagre favoritter</li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2">3.5 Verifiseringsprosess</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn gjør rimelige forsøk på å verifisere arbeidsgiverprofiler, men kan ikke <strong>garantere at alle profiler representerer legitime bedriftsrepresentanter</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Verifisering innebærer ikke at Talentfunn bekrefter din myndighet til å representere bedriften – dette er <strong>ditt ansvar</strong>.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">3.6 Ansvar for urettmessig representasjon</h3>
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-3">
                  <p className="text-red-900 font-medium mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden />
                    Urettmessig representasjon av en bedrift er alvorlig
                  </p>
                  <p className="text-red-800 text-sm leading-relaxed">
                    Hvis du oppretter en profil uten å ha myndighet til å representere bedriften, kan dette utgjøre <strong>identitetstyveri</strong> eller <strong>bedrageri</strong>, og kan være i strid med <strong>markedsføringsloven</strong>.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Du holder Talentfunn <strong>skadesløs</strong> for alle krav, tap, skader eller kostnader som oppstår som følge av din urettmessige representasjon av en bedrift.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">3.7 Talentfunns rett til å slette kontoer</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Talentfunn forbeholder seg retten til å:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                  <li><strong>Avvise</strong> arbeidsgiverprofiler uten begrunnelse</li>
                  <li><strong>Slette</strong> kontoer ved mistanke om urettmessig representasjon</li>
                  <li><strong>Kontakte bedriften</strong> direkte for å bekrefte representasjon</li>
                  <li><strong>Rapportere</strong> mistenkelig aktivitet til myndighetene</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Sletting kan skje <strong>uten forvarsel</strong> og uten refusjon av betalte abonnementer.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">3.8 Bedriftens ansvar</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Bedrifter som oppdager at noen urettmessig representerer dem på Talentfunn, oppfordres til å kontakte oss umiddelbart på <a href="mailto:kontakt@talentfunn.no" className="text-purple-600 underline">kontakt@talentfunn.no</a>.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">3.9 Bedriftskjøp og MVA</h3>
                <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-3">
                  <p className="text-gray-800 font-medium mb-2 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 flex-shrink-0" aria-hidden />
                    Arbeidsgiver-abonnementer er kun tilgjengelig som bedriftskjøp
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Ved kjøp av abonnement må du oppgi bedriftsinformasjon (organisasjonsnummer) til vår betalingsleverandør (Nets/Nexi). Dette sikrer korrekt MVA-håndtering og at bedriften mottar faktura.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Privatpersoner kan ikke kjøpe arbeidsgiver-abonnement. Alle kjøp anses som næringskjøp (B2B) og har derfor ikke 14 dagers angrefrist.
                </p>
              </section>

              {/* User Content */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">4. Brukerinnhold</h2>
                <h3 className="text-xl text-purple-800 mb-2">4.1 Ditt ansvar</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Du er alene ansvarlig for alt innhold du publiserer på Talentfunn, inkludert profilbeskrivelser, bilder, portfolioprosjekter og meldinger.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">4.2 Forbudt innhold</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Talentfunn er en plattform for alle aldre, og du samtykker i IKKE å publisere innhold som:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Er ulovlig, truende, trakasserende, ærekrenkende eller hatefullt</li>
                  <li>Krenker andres immaterielle rettigheter eller personvern</li>
                  <li><strong>Inneholder nakenhet, pornografi, seksuelt eksplisitt materiale eller seksuelt innhold av noen art</strong></li>
                  <li>Inneholder vold eller fremmer voldelig atferd</li>
                  <li>Inneholder spam, markedsføring eller uønsket reklame</li>
                  <li>Inneholder virus, malware eller annen skadelig kode</li>
                  <li>Er villedende eller bedragersk</li>
                  <li>Er upassende for et familievennlig miljø</li>
                </ul>
                <p className="text-red-600 mt-3 font-medium">
                  VIKTIG: Talentfunn har nulltoleranse for seksuelt innhold. Brudd på denne regelen vil føre til umiddelbar og permanent utestengelse.
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">4.3 Lisens</h3>
                <p className="text-gray-700 leading-relaxed">
                  Ved å publisere innhold på Talentfunn, gir du oss en ikke-eksklusiv, global, royaltyfri lisens til å bruke, vise og distribuere ditt innhold i forbindelse med drift av tjenesten. Du beholder alle eierrettigheter til ditt innhold.
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">4.4 Videoupplasting og tredjeparts lagring</h3>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-3">
                  <p className="text-gray-800 font-medium mb-2">
                    Pluss-brukere kan laste opp videoer til sine portfolioprosjekter (maks én video per prosjekt).
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Videoer lagres og streames via tredjepartstjenesten Bunny.net for optimal ytelse og pålitelighet. Ved å laste opp video godtar du at videofilen lagres på Bunny.net sine servere i EU.
                  </p>
                </div>
                
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-3">
                  <p className="text-amber-900 font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden />
                    VIKTIG: Alle videoer er offentlige
                  </p>
                  <p className="text-amber-800 leading-relaxed">
                    <strong>Alle opplastede videoer er offentlig tilgjengelige</strong> og kan ses av alle besøkende på Talentfunn.no uten innlogging. 
                    Du må eksplisitt godta dette ved opplasting. Ikke last opp videoer med personopplysninger du ikke ønsker å dele offentlig.
                  </p>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-2">
                  For videoupplasting gjelder følgende:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Kun Pluss-brukere:</strong> Videoupplasting er kun tilgjengelig for Talent-brukere med aktivt Pluss-abonnement</li>
                  <li><strong>Offentlig tilgang:</strong> Videoer er synlige for alle besøkende på Talentfunn.no (ingen innlogging nødvendig). Dette sikrer at arbeidsgivere kan se portfolioene dine uten hindringer</li>
                  <li><strong>Eksplisitt samtykke:</strong> Du må aktivt godta at videoen blir offentlig synlig før opplasting</li>
                  <li><strong>Ditt ansvar:</strong> Du er alene ansvarlig for alt videoinnhold du laster opp. Alle regler i seksjon 4.2 om forbudt innhold gjelder også for videoer</li>
                  <li><strong>Nulltoleranse:</strong> Upassende videoinnhold vil føre til umiddelbar permanent utestengelse uten varsel</li>
                  <li><strong>Tredjeparts lagring:</strong> Videoer lagres på Bunny.net sine servere. Talentfunn er ikke ansvarlig for Bunny.net sin drift, sikkerhet eller tilgjengelighet</li>
                  <li><strong>GDPR og sletting:</strong> Når du sletter en video fra Talentfunn, slettes den også fra Bunny.net innen 24 timer. Ved kontosletting slettes alle dine videoer permanent</li>
                  <li><strong>Båndbredde:</strong> Videostreaming bruker båndbredde. Vi forbeholder oss retten til å begrense videokvalitet eller tilgang ved misbruk</li>
                </ul>
                
                <p className="text-gray-700 leading-relaxed mt-3 font-medium">
                  Talentfunn fraskriver seg alt ansvar for innhold i brukeropplastede videoer. Vi gjennomgår ikke videoer manuelt før publisering, men reagerer raskt på rapporterte brudd.
                </p>
              </section>

              {/* Subscriptions */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">5. Abonnementer og betaling</h2>
                <h3 className="text-xl text-purple-800 mb-2">5.1 Pluss-abonnement (Talent)</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Talent-brukere kan oppgradere til Pluss-abonnement for 149 kr/mnd som gir:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Ubegrensede portfolioprosjekter</li>
                  <li>Mulighet til å laste opp video i prosjekter (maks én per prosjekt)</li>
                  <li>Fremhevet profil i søkeresultater</li>
                  <li>Prioritert support</li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">5.2 Arbeidsgiver-abonnement</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Arbeidsgiver-brukere må ha aktivt abonnement på 490 kr/mnd for å kunne:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Sende meldinger til talenter</li>
                  <li>Kontakte ubegrenset antall talenter</li>
                  <li>Få tilgang til fullstendige profiler</li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">5.3 Arbeidsgiver Pluss-abonnement</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Arbeidsgiver-brukere kan oppgradere til Pluss-abonnement på 990 kr/mnd som inkluderer alt i standard abonnement, pluss:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Mulighet til å legge ut oppdrag (publiser oppdrag som er synlig for alle brukere)</li>
                  <li>Fremhevet profil i søkeresultater for talenter</li>
                  <li>Prioritert support</li>
                  <li>Tilgang til avanserte søkefiltre og statistikk</li>
                  <li>Mulighet til å merke profiler med notater</li>
                </ul>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">5.4 Betaling og fakturering</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Abonnementer betales månedlig og fornyes automatisk til du avbryter. Betalinger behandles sikkert via Nets/Nexi. Vi lagrer aldri kortinformasjon på våre servere. Ved mislykket trekk (f.eks. utløpt kort) har du inntil 7 dager på deg til å oppdatere betalingsdetaljene før tilgangen opphører; du varsles og får tydelig informasjon om fristen i appen.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">5.5 Kansellering og refusjon</h3>
                <p className="text-gray-700 leading-relaxed">
                  Du kan kansellere abonnementet ditt når som helst via kontoinnstillingene (betalingsportalen). Oppsigelse gjøres uten ekstra kostnad og krever ikke at du kontakter oss. Kanselleringen trer i kraft ved slutten av gjeldende faktureringsperiode. Vi tilbyr ikke refusjon for delvis brukte perioder.
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">5.6 Forbruker vs. næringskjøp (B2C vs. B2B)</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn skiller mellom forbrukerkjøp og næringskjøp:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Forbrukerkjøp (B2C):</strong> Privatpersoner som handler for privat bruk. Se seksjon 5.7 for informasjon om angrerett og unntak for digitale tjenester.</li>
                  <li><strong>Næringskjøp (B2B):</strong> Bedrifter og næringsdrivende som handler på vegne av en virksomhet, har ikke lovfestet angrefrist. Disse avtalene er bindende fra kjøpstidspunktet.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Ved kjøp må du oppgi om du handler som privatperson (forbruker) eller på vegne av en bedrift (næringsdrivende).
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">5.7 Angrerett for forbrukere og unntak</h3>
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-5 rounded-xl border-2 border-blue-200 mb-3">
                  <p className="text-gray-700 leading-relaxed mb-2">
                    Som hovedregel har forbrukere 14 dagers angrerett ved kjøp på nett. Talentfunn Pluss er imidlertid en digital tjeneste som leveres og tas i bruk umiddelbart etter betaling.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    I henhold til Angrerettloven § 22 bokstav n, bortfaller angreretten når leveringen av den digitale tjenesten har begynt. Ved å gjennomføre kjøpet av et Pluss-abonnement, samtykker du uttrykkelig til at tjenesten leveres umiddelbart, og du erkjenner samtidig at angreretten din dermed går tapt.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Selv om angreretten bortfaller ved kjøp, står du fritt til å si opp abonnementet ditt når som helst, slik at det ikke fornyes for neste periode (se punkt 5.5).
                  </p>
                </div>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">5.8 Reklamasjonshåndtering</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Forbrukere har reklamasjonsrett ved mangel i henhold til Forbrukerkjøpsloven kapittel 6. Ved reklamasjon, ta kontakt med oss på <a href="mailto:kontakt@talentfunn.no" className="text-purple-600 underline">kontakt@talentfunn.no</a> med beskrivelse av problemet. Vi vil behandle reklamasjonen i tråd med gjeldende lov og retningslinjer. For digitale tjenester som abonnement gjelder særlige regler; vi svarer på reklamasjoner innen rimelig tid.
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">5.9 Prisendringer</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn forbeholder seg retten til å endre prisene på våre abonnementer.
                </p>
                
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-3">
                  <h4 className="text-lg text-blue-900 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4 flex-shrink-0" aria-hidden /> Varsling:
                  </h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 text-sm">
                    <li>Vi varsler alltid om prisendringer <strong>minst 30 dager før</strong> de trer i kraft</li>
                    <li>Varselet sendes til din <strong>registrerte e-postadresse</strong></li>
                    <li>Varselet vil tydelig angi den nye prisen og datoen endringen trer i kraft</li>
                  </ul>
                </div>

                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-3">
                  <h4 className="text-lg text-green-900 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" aria-hidden /> Din rett til å si opp:
                  </h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 text-sm">
                    <li>Du har rett til å si opp abonnementet <strong>kostnadsfritt</strong> før prisendringen trer i kraft</li>
                    <li>Oppsigelse gjøres via kontoinnstillingene eller ved å kontakte oss på <a href="mailto:kontakt@talentfunn.no" className="text-green-700 underline">kontakt@talentfunn.no</a></li>
                    <li>Hvis du ikke sier opp innen fristen, godtar du automatisk den nye prisen og abonnementet fortsetter til ny pris</li>
                  </ul>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  <strong>Gjeldende pris:</strong> Din nåværende pris fortsetter å gjelde frem til endringsdatoen. Allerede betalte perioder påvirkes ikke av prisendringen.
                </p>
              </section>

              {/* Communication */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">6. Kommunikasjon og meldinger</h2>
                <h3 className="text-xl text-purple-800 mb-2">6.1 Meldingssystemet</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn tilbyr et internt meldingssystem for kommunikasjon mellom brukere. Arbeidsgivere med aktivt abonnement kan starte nye samtaler. Talent-brukere kan svare på mottatte meldinger.
                </p>

                <h3 className="text-xl text-purple-800 mb-2">6.2 Varsler og notifikasjoner</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn sender varsler for å holde deg oppdatert om aktivitet på plattformen. Dette inkluderer:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>In-app varsler:</strong> Varsler som vises når du er innlogget. Disse er alltid aktivert.</li>
                  <li><strong>Push-notifikasjoner:</strong> Varsler på din enhet når du ikke har appen åpen. Krever din eksplisitte tillatelse.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Du kan når som helst endre dine varslingsinnstillinger i kontoinnstillingene. Push-notifikasjoner krever tillatelse fra nettleseren, som du kan trekke tilbake når som helst.
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">6.3 Administrative varsler</h3>
                <p className="text-gray-700 leading-relaxed">
                  Vi forbeholder oss retten til å sende viktige servicevarsler til alle brukere, inkludert men ikke begrenset til sikkerhetsvarsler, vedlikeholdsmeldinger og kritiske oppdateringer om tjenesten. Disse varslene kan ikke deaktiveres da de er nødvendige for drift av tjenesten.
                </p>

                <h3 className="text-xl text-purple-800 mb-2 mt-4">6.4 Profesjonell tone</h3>
                <p className="text-gray-700 leading-relaxed">
                  All kommunikasjon gjennom plattformen skal være profesjonell og respektfull. Trakassering, hatefulle ytringer eller upassende kommunikasjon er strengt forbudt og kan føre til umiddelbar utestengelse.
                </p>
              </section>

              {/* Prohibited Use */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">7. Forbudt bruk</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Du samtykker i IKKE å:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Bruke tjenesten til ulovlige formål</li>
                  <li>Trakassere, mobbe eller true andre brukere</li>
                  <li>Utgi deg for å være en annen person eller organisasjon</li>
                  <li>Samle inn personopplysninger om andre brukere</li>
                  <li>Forsøke å få uautorisert tilgang til systemet</li>
                  <li>Bruke automatiserte verktøy (bots) uten tillatelse</li>
                  <li>Omgå tekniske begrensninger i tjenesten</li>
                  <li>Videresende passord eller gi andre tilgang til din konto</li>
                </ul>
              </section>

              {/* Intellectual Property */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">8. Immaterielle rettigheter</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn og dets originale innhold, funksjoner og funksjonalitet eies av oss og er beskyttet av norske og internasjonale lover om opphavsrett, varemerker og andre immaterielle rettigheter.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Du kan ikke kopiere, modifisere, distribuere eller selge noen del av tjenesten uten vår skriftlige tillatelse.
                </p>
              </section>

              {/* Termination */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">9. Oppsigelse og suspensjon</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Vi forbeholder oss retten til å suspendere eller avslutte din konto umiddelbart, uten forvarsel eller ansvar, av enhver grunn, inkludert men ikke begrenset til brudd på disse vilkårene.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Ved oppsigelse vil din rett til å bruke tjenesten opphøre umiddelbart. Du kan når som helst slette din konto via kontoinnstillingene.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Alle bestemmelser i disse vilkårene som etter sin natur bør overleve oppsigelse, vil overleve oppsigelse, inkludert, men ikke begrenset til, eierbestemmelser, garantifraskrivelser, erstatning og ansvarsbegrensninger.
                </p>
              </section>

              {/* Disclaimer */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">10. Ansvarsfraskrivelse</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Talentfunn er kun en formidlingsplattform og er ikke part i eventuelle arbeidsavtaler, oppdragsavtaler eller kontrakter som inngås mellom brukere. Alt ansvar for lønn, skattetrekk, forsikringer og arbeidsrettslige forhold ligger hos brukerne selv.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Brukere er selv ansvarlige for å rapportere inntekt og betale eventuelle skatter og avgifter knyttet til oppdrag funnet via tjenesten.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Tjenesten leveres "som den er" og "som tilgjengelig" uten garantier av noen art, verken uttrykkelige eller underforståtte.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Vi garanterer ikke at tjenesten vil være uavbrutt, sikker eller feilfri. Vi er ikke ansvarlige for innhold publisert av brukere eller for eventuelle skader som følge av bruk av tjenesten.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Vi anbefaler at du alltid utviser forsiktighet når du kommuniserer med andre brukere og møter folk i virkeligheten.
                </p>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">11. Ansvarsbegrensning</h2>
                <p className="text-gray-700 leading-relaxed">
                  I den utstrekning loven tillater, skal vi ikke være ansvarlige for noen indirekte, tilfeldige, spesielle, følgeskader eller straffskader, inkludert tap av fortjeneste, data, bruk, goodwill eller andre immaterielle tap, som følge av:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-3">
                  <li>Din tilgang til eller bruk av (eller manglende evne til å bruke) tjenesten</li>
                  <li>Eventuelle oppførsel eller innhold fra tredjepart på tjenesten</li>
                  <li>Uautorisert tilgang, bruk eller endring av dine overføringer eller innhold</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Denne ansvarsbegrensningen gjelder så langt den ikke strider mot preseptorisk (ufravikelig) norsk lov, herunder ansvar for skade forårsaket ved forsett eller grov uaktsomhet fra Talentfunns side.
                </p>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">12. Gjeldende lov</h2>
                <p className="text-gray-700 leading-relaxed">
                  Disse vilkårene skal reguleres og tolkes i samsvar med norsk lov. Eventuelle tvister skal løses i norske domstoler.
                </p>
              </section>

              {/* Changes to Terms */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">13. Endringer i vilkår</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Vi forbeholder oss retten til å endre disse vilkårene til enhver tid. Vesentlige endringer varsles minst 30 dager før de trer i kraft, ved melding på tjenesten og/eller e-post til din registrerte adresse.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Din fortsatte bruk av tjenesten etter at endringene har trådt i kraft, utgjør din aksept av de nye vilkårene.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">14. Kontakt oss</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Hvis du har spørsmål om disse vilkårene, vennligst kontakt oss:
                </p>
                <div className="bg-purple-50 p-4 rounded-xl border-2 border-purple-100">
                  <p className="text-gray-700">
                    <strong>Evensen Solutions</strong>
                  </p>
                  <p className="text-gray-700 mt-1">
                    <strong>Adresse:</strong> Blåknappveien 9, 4823 Nedenes
                  </p>
                  <p className="text-gray-700 mt-1">
                    <strong>Org.nr:</strong> 933 184 048
                  </p>
                  <p className="text-gray-700 mt-2">
                    <strong>E-post:</strong> kontakt@talentfunn.no
                  </p>
                  <p className="text-gray-700 mt-2">
                    Vi svarer så fort vi kan.
                  </p>
                </div>
              </section>

              {/* Summary */}
              <section className="border-t-2 border-purple-200 pt-6 mt-8">
                <h2 className="text-2xl text-purple-900 mb-3">Oppsummering</h2>
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-6 rounded-2xl">
                  <p className="text-gray-800 mb-3">
                    Ved å bruke Talentfunn godtar du å:
                  </p>
                  <ul className="space-y-2 text-gray-800">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span>Opptre profesjonelt og respektfullt overfor andre brukere</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span>Ikke publisere ulovlig eller støtende innhold</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span>Betale for abonnementer i henhold til prislisten</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span>Beskytte din kontoinformasjon</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span>Akseptere at vi kan suspendere eller slette kontoen din ved brudd på vilkårene</span>
                    </li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
      </PageShell>
    </div>
  );
}
